/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-console */
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { get } from 'lodash-es';
import { stringify } from 'query-string';
import React, { useEffect, useRef } from 'react';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import {
  API_LINE_ITEM_URL,
  API_LINE_ITEMS_URL
} from '../../constants/apiUrls';
import { contractStepLabels } from '../../constants/contracts';
import { lineItemFormText, lineItemStatusMap, StatusType } from '../../constants/lineItems';
import { ADMIN_CONTRACT_EDIT_URL, EDIT_LINE_ITEM_URL } from '../../constants/routeUrls';
import { backgroundColor } from '../../constants/style';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import useLineItems from '../../hooks/useLineItems';
import useModal from '../../hooks/useModal';
import { useSnackBar } from '../../hooks/useSnackBar';
import useVersions, { ModelType } from '../../hooks/useVersions';
import { ContractStep } from '../../pages/ContractPage';
import mapContractToForm from '../../util/mapContractToForm';
import mapFormToLineItem from '../../util/mapFormToLineItem';
import mapLineItemToForm from '../../util/mapLineItemToForm';
import { postRequestJSON, putRequestJSON } from '../../util/services';
import LineItemSchema, { LineItem } from '../../validationSchemas/LineItemSchema';
import ContractConfigurationBreadcrumb from '../Contracts/ContractConfigurationBreadcrumb';
import ContractReportsPageLink from '../Contracts/ContractReportsPageLink';
import FormEndOptions from '../forms/FormEndOptions';
import SimpleStepper from '../forms/SimpleStepper';
import LookerLink from '../shared/LookerLink';
import DuplicateLineItemForm from './DuplicateLineItemForm';
import LineItemBudgetRates from './LineItemBudgetRatesSection';
import LineItemMediaSection from './LineItemMediaSection';
import LineItemObjectivesSection from './LineItemObjectivesSection';
import LineItemReportingSection from './LineItemReportingSection';
import LineItemTimingSection from './LineItemTimingSection';

interface LineItemFormProps {
  isNewLineItem: boolean;
  lineItemData: LineItem;
}

function LineItemForm(props: LineItemFormProps) {
  const { isNewLineItem, lineItemData } = props;
  const formOpenedAt = useRef(Date.now());
  const { apiHost } = useConfig();
  const params = useParams();
  const navigate = useNavigate();
  const contractConfigurationContext = useContractConfigurationContext();
  const contractData = get(contractConfigurationContext, 'contractData', {});
  const { contractId } = mapContractToForm(contractData);
  const queryClient = useQueryClient();
  const lineItems = useLineItems(Number(params.id));
  const { showSnackBar } = useSnackBar();
  const mainBodyWidth = get(contractConfigurationContext, 'mainBodyWidth');

  const goToContractPage = () => navigate(generatePath(ADMIN_CONTRACT_EDIT_URL, { id: params.id }));

  const form: UseFormReturn<LineItem> = useForm<LineItem>({
    resolver: zodResolver(LineItemSchema),
    defaultValues: lineItemData,
  });

  const {
    formState: { errors, dirtyFields, isDirty },
    handleSubmit,
    getValues,
    reset,
  } = form;

  useConfirmNavChange(() => isDirty);

  useEffect(() => {
    if (!isDirty) {
      reset(lineItemData);
    }
  }, [lineItemData, reset, isDirty]);

  // loop over API error strings in order to attribute them to the correct field
  const mapAPIErrorsToFields = (apiErrorMessages: string[]) => {
    showSnackBar(`${apiErrorMessages.join('. ')}.`, 'error', {
      event: 'line_item_save_error',
      contract_id: contractId,
    });
    return apiErrorMessages;
  };

  const handleFormSubmit = async (data: any) => {
    const allValues = getValues() as Record<string, any>;
    const submissionBody = mapFormToLineItem({
      ...data,
      customDeliveryProfiles: allValues.customDeliveryProfiles || [],
      deletedProfileIds: allValues.deletedProfileIds || [],
    });

    let response;
    if (isNewLineItem) {
      response = await postRequestJSON(
        apiHost,
        generatePath(API_LINE_ITEMS_URL, { contractId: contractId.toString(), '*': undefined }),
        submissionBody
      );
    } else {
      response = await putRequestJSON(
        apiHost,
        generatePath(API_LINE_ITEM_URL, {
          lineItemId: data.lineItemId.toString(),
          '*': `?${stringify({
            contract_id: contractId,
          })}`,
        }),
        submissionBody
      );
    }

    if (response.errors) {
      // api errors
      mapAPIErrorsToFields(response.errors);
    } else if (response.model?.id) {
      showSnackBar('Line Item Saved!', 'success', {
        event: isNewLineItem ? 'line_item_created' : 'line_item_updated',
        line_item_id: response.model.id,
        line_item_name: response.model.name,
        contract_id: contractId,
        formOpenedAt: formOpenedAt.current,
      });

      // Reload the form with latest data from the server (PUT response includes all profiles).
      lineItems.refetch();
      reset(mapLineItemToForm(response.model));
      // Update line item cache so lineItemData prop stays in sync (avoids effect overwriting form with stale data).
      if (!isNewLineItem && params.lineItemId) {
        queryClient.setQueryData(['lineItem', Number(params.lineItemId)], response.model);
      }

      if (isNewLineItem) {
        // Wait 1 sec so the form has time to reset the isDirty flag so it will not ask to confirm changes.
        setTimeout(() => {
          // successful submission
          navigate(generatePath(EDIT_LINE_ITEM_URL, { id: params.id, lineItemId: response.model.id }));
        }, 1000);
      }
    } else {
      // unhandled error or 500
      const pageError = 'Something went wrong on this page! Please share this error with #special-ops.';
      showSnackBar(`${pageError} ${JSON.stringify(response)} ${JSON.stringify(submissionBody)}`, 'error', {
        event: 'line_item_save_error',
      });
    }
  };

  const [showModal, hideModal] = useModal(
    () => <DuplicateLineItemForm lineItem={lineItemData} onClose={hideModal} />,
    [lineItemData]
  );

  const { openVersionsModal } = useVersions(ModelType.LineItem, Number(params.lineItemId));

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 1,
          left: 0,
          zIndex: 10,
          backgroundColor: '#F9F9F9',
        }}
      >
        <ContractConfigurationBreadcrumb
          title={[<span>{isNewLineItem ? 'New Line Item' : lineItemData.lineItemName}</span>]}
        />
        <Stack direction="row" mx={2} alignItems="center" justifyContent="space-between">
          <Stack spacing={2} direction="row" alignItems="center">
            <Typography variant="h5">
              {isNewLineItem ? lineItemFormText.lineItemHeader.new : lineItemFormText.lineItemHeader.edit}
            </Typography>
            <Stack spacing={1} direction="row" alignItems="center">
              <Typography variant="body2">{lineItemFormText.lineItemHeader.status}</Typography>
              <Chip
                label={lineItemStatusMap.incomplete[0]}
                size="small"
                color={lineItemStatusMap.incomplete[2] as StatusType}
              />
              {!isNewLineItem && (
                <Button variant="text" onClick={openVersionsModal}>
                  {lineItemFormText.changelogButtonLabel}
                </Button>
              )}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <ContractReportsPageLink contractId={lineItemData.contractId} />
            <LookerLink lineItemId={Number(params.lineItemId)} />
          </Stack>
        </Stack>
        <Stack mx={1} my={2}>
          <SimpleStepper
            stepLabels={contractStepLabels}
            activeStep={ContractStep.Configuration}
            callbacks={[goToContractPage]}
          />
        </Stack>
      </Stack>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(handleFormSubmit, (validationErrors) => {
          console.log('Form validation errors:', validationErrors);
          showSnackBar('Please fix the validation errors before saving.', 'error', {
            event: 'line_item_validation_error',
            contract_id: contractId,
          });
        })} autoComplete="off">
          <Stack p={2} pt={1} pb={10} spacing={2}>
            <LineItemObjectivesSection />
            <LineItemMediaSection />
            <LineItemTimingSection lineItemData={lineItemData} />
            <LineItemBudgetRates />
            <LineItemReportingSection />
          </Stack>
          <FormEndOptions position="fixed" width={`${mainBodyWidth}px`}>
            <Button variant="outlined" onClick={showModal}>
              Duplicate
            </Button>
            <Button type="submit" color="primary" disabled={!isDirty}>
              {isNewLineItem ? lineItemFormText.lineItemNewSave : lineItemFormText.lineItemEditSave}
            </Button>
            {apiHost.includes('-qa') && (
              <Stack spacing={2} direction="row" sx={{ backgroundColor, border: '2px dotted #b7b7b7', px: 2 }}>
                <Button type="submit" variant="outlined" color="primary">
                  hookFormSubmit
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    console.log(' ----- getValues');
                    console.log(getValues());
                    console.log(' ----- isDirty');
                    console.log(isDirty);
                    console.log(' ----- dirtyFields');
                    console.log(dirtyFields);
                    console.log(' ----- errors');
                    console.log(errors);
                  }}
                >
                  checkFormState
                </Button>
              </Stack>
            )}
          </FormEndOptions>
        </form>
      </FormProvider>
    </>
  );
}

export default LineItemForm;
